/**
 * Pure helpers for resolving the identifier sent when adding a lead.
 *
 * Kept out of the component so the precedence logic is unit-testable without
 * rendering. Used by AddToWatchlistV2.
 */

/**
 * Picks the most precise identifier for a search result: CIN, then PAN, then a
 * caller-supplied fallback (typically the org name).
 *
 * Note on GSTIN: a GSTIN typed in the search bar is converted to its PAN
 * server-side (globalSearchV2), so a GSTIN-originated result arrives here with
 * `pan` populated — no special-casing needed.
 */
export const extractIdentifier = (result: any, fallback?: string): string => result?.cinNumber || result?.pan || fallback || '';

export interface ResolveAddLeadIdentifierArgs {
    /** True only for a genuine "add by typed text" flow (no real entity picked). */
    isAddUsingFlow: boolean;
    /** The name currently shown in the identifier input. */
    displayName?: string;
    /** Identifier captured when a real entity was picked (CIN/PAN). */
    payloadIdentifier?: string;
    /** Legal name resolved from prefill, if any. */
    legalNameFromPrefill?: string;
}

/**
 * Resolves the `identifier` value sent to addCompanyToWatchlist.
 *
 * - Genuine add-by-typed-text (`isAddUsingFlow`) → the typed display name.
 * - Otherwise (a real entity was picked, which resets the flag) → the captured
 *   payload identifier (CIN/PAN), then the prefill legal name, then the display
 *   name as a last resort.
 *
 * Returns '' when nothing resolves; callers omit the field on a falsy value.
 */
export const resolveAddLeadIdentifier = ({
    isAddUsingFlow,
    displayName,
    payloadIdentifier,
    legalNameFromPrefill,
}: ResolveAddLeadIdentifierArgs): string => (isAddUsingFlow ? displayName : payloadIdentifier || legalNameFromPrefill || displayName) || '';
